'use client';

import Button from '@egalite/ui/Buttons';
import Typography from '@egalite/ui/Typography';
import clx from 'classnames';
import Cookies from 'js-cookie';
import { FC, FormEvent, useId, useState } from 'react';
import authFetch from '../../lib/auth/authFetch';
import clearSessionCookie from '../../lib/auth/clearSessionCookie';

/** A mesma palavra que a API exige (`apps/api/src/lib/candidateAccountDeletion.ts`). */
export const CONFIRMATION_WORD = 'EXCLUIR';

const GENERIC_ERROR = 'Não foi possível registrar o pedido agora. Tente de novo em alguns minutos.';

type Props = {
	onCancel: () => void;
	/** Depois do pedido registrado: a sessão já não vale, então sai da área logada. */
	onDeleted?: () => void;
};

/**
 * Pedido de exclusão da conta pelo próprio candidato (Hub #2415). Só registra o pedido; quem apaga
 * tudo é o job do egalite-task-schedule, em poucos minutos. A partir do pedido a conta não entra mais.
 */
const DeleteAccount: FC<Props> = ({ onCancel, onDeleted = () => window.location.replace('/conta-excluida') }) => {
	const [typed, setTyped] = useState('');
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const warningId = useId();
	const inputId = useId();

	const confirmed = typed.trim().toUpperCase() === CONFIRMATION_WORD;

	const finish = () => {
		clearSessionCookie();
		onDeleted();
	};

	const submit = async (event: FormEvent) => {
		event.preventDefault();
		if (!confirmed || sending) return;

		setSending(true);
		setError(null);

		try {
			const res = await authFetch('/api/v2/candidate/account/delete-request', Cookies.get('token'), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ confirmation: typed }),
			});
			const body = await res.json().catch(() => null);

			// 401 "Conta em exclusão": o pedido já tinha sido feito (outra aba, clique duplo).
			if (res.ok && body?.success === true) {
				finish();
				return;
			}
			if (res.status === 401 && body?.message === 'Conta em exclusão') {
				finish();
				return;
			}

			setError(body?.message ?? GENERIC_ERROR);
		} catch {
			setError(GENERIC_ERROR);
		}

		setSending(false);
	};

	return (
		<form onSubmit={submit} className="flex flex-col gap-8 px-8 pb-12 md:px-16" noValidate>
			<Typography variant="headline-4" md="headline-3" component="h2">
				Excluir minha conta
			</Typography>

			<div id={warningId} className="flex flex-col gap-4">
				<Typography variant="body-1">Ao excluir sua conta, apagamos de forma definitiva:</Typography>
				<ul className="list-disc pl-10 flex flex-col gap-2 text-base-grey-5">
					<Typography variant="body-1" component="li">
						seu perfil e seu currículo;
					</Typography>
					<Typography variant="body-1" component="li">
						seu laudo e sua foto;
					</Typography>
					<Typography variant="body-1" component="li">
						suas candidaturas às vagas e o contato liberado para empresas.
					</Typography>
				</ul>
				<Typography variant="body-1" className="font-bold">
					Essa ação não pode ser desfeita. Para voltar a usar a IncluiPCD, será preciso criar uma conta nova.
				</Typography>
				<Typography variant="body-2">
					Candidaturas que você fez direto no site de outra empresa (como Gupy) ficam com essa empresa e não são apagadas
					por nós.
				</Typography>
			</div>

			<div className="flex flex-col gap-2">
				<label htmlFor={inputId}>
					<Typography variant="body-1" component="span">
						Para confirmar, digite <strong>{CONFIRMATION_WORD}</strong>
					</Typography>
				</label>
				<input
					id={inputId}
					type="text"
					autoFocus
					autoComplete="off"
					autoCapitalize="characters"
					spellCheck={false}
					value={typed}
					onChange={event => setTyped(event.target.value)}
					aria-describedby={warningId}
					className={clx(
						'text-[1.8rem] font-roboto font-normal w-full h-20 px-4 rounded-2xl border focus:outline-none',
						confirmed
							? 'border-[#C62828] bg-error-light text-[#C62828] focus:border-[#C62828]'
							: 'border-base-grey-5 text-base-grey-5 bg-transparent hover:border-brand-main focus:border-brand-main focus:bg-brand-light',
					)}
				/>
			</div>

			{error ? (
				<div role="alert" className="bg-error-light border-l-4 border-error-main p-4 rounded">
					<Typography variant="body-2" className="text-error-dark">
						{error}
					</Typography>
				</div>
			) : null}

			<div className="flex flex-col-reverse gap-4 md:flex-row md:justify-end">
				<Button type="button" variant="secondary" onClick={onCancel} disabled={sending}>
					Cancelar
				</Button>
				<Button
					type="submit"
					disabled={!confirmed || sending}
					aria-busy={sending}
					className={clx(
						confirmed &&
							!sending &&
							'!bg-[#C62828] !border-[#C62828] hover:!bg-[#A31F1F] hover:!border-[#A31F1F] focus:!outline-[#C62828]/50',
					)}
				>
					{sending ? 'Excluindo...' : 'Excluir minha conta definitivamente'}
				</Button>
			</div>
		</form>
	);
};

export default DeleteAccount;
